import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { toast } from '../lib/safeToast';
import {
  ArrowUpRight,
  ArrowDownRight,
  Search,
  Download,
  ChevronLeft,
  ChevronRight,
  Calendar,
  X,
  Filter
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { format } from 'date-fns';
import Papa from 'papaparse';
import { saveAs } from 'file-saver';

const Transactions = () => {
  const { api } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({});
  const [filters, setFilters] = useState({
    page: 1,
    per_page: 20,
    type: 'all',
    from_date: null,
    to_date: null,
  });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchTransactions();
  }, [filters]);

  const fetchTransactions = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('page', filters.page);
      params.append('per_page', filters.per_page);
      if (filters.type !== 'all') params.append('type', filters.type);
      if (filters.from_date) params.append('from_date', format(filters.from_date, 'yyyy-MM-dd'));
      if (filters.to_date) params.append('to_date', format(filters.to_date, 'yyyy-MM-dd'));

      const response = await api.get(`/banking/transactions?${params.toString()}`);
      setTransactions(response.data.data || []);
      setMeta(response.data.meta || {});
    } catch (error) {
      toast.error('Failed to fetch transactions');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    const num = parseFloat(amount);
    return new Intl.NumberFormat('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-AU', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleExport = async (exportFormat) => {
    try {
      const response = await api.get(`/export/transactions?format=${exportFormat}`);
      const data = response.data.data;

      if (exportFormat === 'csv') {
        const csv = Papa.unparse(data);
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
        saveAs(blob, `transactions_${format(new Date(), 'yyyy-MM-dd')}.csv`);
      } else {
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
        saveAs(blob, `transactions_${format(new Date(), 'yyyy-MM-dd')}.json`);
      }
      toast.success('Export successful');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const filteredTransactions = transactions.filter((tx) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      tx.counterparty?.toLowerCase().includes(term) ||
      tx.reference?.toLowerCase().includes(term) ||
      tx.id?.toLowerCase().includes(term)
    );
  });

  const clearFilters = () => {
    setFilters({
      page: 1,
      per_page: 20,
      type: 'all',
      from_date: null,
      to_date: null,
    });
    setSearchTerm('');
  };

  const getCurrencyBadgeClass = (currency) => {
    const classes = {
      AUD: 'bg-[#E6F7F3] text-[#1C472B]',
      USD: 'bg-blue-50 text-blue-700',
      EUR: 'bg-purple-50 text-purple-700',
      USDC: 'bg-cyan-50 text-cyan-700',
      USDT: 'bg-emerald-50 text-emerald-700',
    };
    return classes[currency] || 'bg-slate-100 text-slate-700';
  };

  return (
    <Layout>
      <div data-testid="transactions-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-extrabold text-[#0F172A] ">
              Transactions
            </h1>
            <p className="text-slate-500 mt-1">View and manage your transaction history</p>
          </div>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="h-11 px-5 rounded-full" data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-44 p-2" align="end">
              <button
                onClick={() => handleExport('csv')}
                className="w-full px-3 py-2.5 text-left text-sm hover:bg-slate-100 rounded-lg font-medium"
              >
                Export as CSV
              </button>
              <button
                onClick={() => handleExport('json')}
                className="w-full px-3 py-2.5 text-left text-sm hover:bg-slate-100 rounded-lg font-medium"
              >
                Export as JSON
              </button>
            </PopoverContent>
          </Popover>
        </div>

        {/* Filters */}
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm">
          <CardContent className="p-5">
            <div className="flex flex-wrap items-center gap-4">
              {/* Search */}
              <div className="relative flex-1 min-w-[240px]">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                <Input
                  placeholder="Search by counterparty or reference..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="h-11 pl-11 pr-4 rounded-full border-slate-200 bg-slate-50 focus:bg-white focus:border-[#1C472B] focus:ring-1 focus:ring-[#1C472B]"
                  data-testid="search-input"
                />
              </div>

              {/* Type Filter */}
              <Select
                value={filters.type}
                onValueChange={(value) => setFilters({ ...filters, type: value, page: 1 })}
              >
                <SelectTrigger className="w-44 h-11 rounded-full border-slate-200 bg-slate-50" data-testid="type-filter">
                  <Filter className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="deposit">Deposits Only</SelectItem>
                  <SelectItem value="withdrawal">Withdrawals Only</SelectItem>
                </SelectContent>
              </Select>

              {/* Date Range */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-11 px-4 rounded-full" data-testid="from-date-btn">
                    <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                    {filters.from_date ? format(filters.from_date, 'MMM d, yyyy') : 'From Date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={filters.from_date}
                    onSelect={(date) => setFilters({ ...filters, from_date: date, page: 1 })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-11 px-4 rounded-full" data-testid="to-date-btn">
                    <Calendar className="w-4 h-4 mr-2 text-slate-400" />
                    {filters.to_date ? format(filters.to_date, 'MMM d, yyyy') : 'To Date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <CalendarComponent
                    mode="single"
                    selected={filters.to_date}
                    onSelect={(date) => setFilters({ ...filters, to_date: date, page: 1 })}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>

              {/* Clear Filters */}
              {(filters.type !== 'all' || filters.from_date || filters.to_date || searchTerm) && (
                <Button
                  variant="ghost"
                  onClick={clearFilters}
                  className="h-11 px-4 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-full"
                  data-testid="clear-filters"
                >
                  <X className="w-4 h-4 mr-1" />
                  Clear
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Transactions Table */}
        <Card className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden">
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-slate-200 border-t-[#1C472B]" />
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="transactions-table">
                    <thead>
                      <tr className="border-b border-slate-100">
                        <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Type</th>
                        {/* Payment Type – uncomment if needed later
                        <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Payment Type</th>
                        */}
                        <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Reference</th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Counterparty</th>
                        <th className="text-left px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                        <th className="text-right px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Amount</th>
                        <th className="text-center px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Currency</th>
                        <th className="text-center px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTransactions.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center py-16 text-slate-500">
                            <div className="flex flex-col items-center">
                              <div className="w-16 h-16 bg-slate-50 rounded-full flex items-center justify-center mb-4">
                                <Search className="w-8 h-8 text-slate-300" />
                              </div>
                              <p className="font-medium">No transactions found</p>
                              <p className="text-sm text-slate-400 mt-1">Try adjusting your filters</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        filteredTransactions.map((tx) => (
                          <tr key={tx.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                                    tx.direction === 'credit'
                                      ? 'bg-[#E6F7F3]'
                                      : 'bg-red-50'
                                  }`}
                                >
                                  {tx.direction === 'credit' ? (
                                    <ArrowDownRight className="w-5 h-5 text-[#1C472B]" />
                                  ) : (
                                    <ArrowUpRight className="w-5 h-5 text-[#F08080]" />
                                  )}
                                </div>
                                <div>
                                  <p className="font-semibold text-[#0F172A] capitalize">
                                    {tx.type}
                                  </p>
                                  <p className="text-xs text-slate-400 font-mono">{tx.id}</p>
                                </div>
                              </div>
                            </td>
                            {/* Payment Type – uncomment if needed later
                            <td className="px-6 py-4">
                              <span className="inline-flex px-3 py-1 rounded-lg text-xs font-medium bg-slate-100 text-slate-700">
                                {tx.payment_type || 'Bank Transfer'}
                              </span>
                            </td>
                            */}
                            <td className="px-6 py-4">
                              <span className="font-mono text-sm text-slate-600">{tx.reference || '—'}</span>
                            </td>
                            <td className="px-6 py-4">
                              <span className="font-medium text-slate-700">{tx.counterparty}</span>
                            </td>
                            <td className="px-6 py-4">
                              <div>
                                <p className="text-sm font-medium text-slate-700">{formatDate(tx.date)}</p>
                                <p className="text-xs text-slate-400">{formatTime(tx.date)}</p>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-right">
                              <span
                                className={`text-lg font-bold ${
                                  tx.direction === 'credit' ? 'text-[#1C472B]' : 'text-[#F08080]'
                                }`}
                              >
                                {tx.direction === 'credit' ? '+' : '-'}
                                {formatCurrency(tx.amount)}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold ${getCurrencyBadgeClass(tx.currency)}`}>
                                {tx.currency}
                              </span>
                            </td>
                            <td className="px-6 py-4 text-center">
                              <span
                                className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold capitalize ${
                                  tx.status === 'completed'
                                    ? 'bg-[#E6F7F3] text-[#1C472B]'
                                    : tx.status === 'pending'
                                    ? 'bg-amber-50 text-amber-700'
                                    : 'bg-red-50 text-[#F08080]'
                                }`}
                              >
                                {tx.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {meta.total > 0 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-slate-100 bg-slate-50/50">
                    <p className="text-sm text-slate-500">
                      Showing <span className="font-semibold text-slate-700">{meta.from || 1}</span> to{' '}
                      <span className="font-semibold text-slate-700">{meta.to || filteredTransactions.length}</span> of{' '}
                      <span className="font-semibold text-slate-700">{meta.total || filteredTransactions.length}</span> transactions
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFilters({ ...filters, page: filters.page - 1 })}
                        disabled={filters.page === 1}
                        className="h-9 w-9 p-0 rounded-full"
                        data-testid="prev-page"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm text-slate-600 px-3 font-medium">
                        Page {filters.page} of {meta.last_page || 1}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
                        disabled={filters.page >= (meta.last_page || 1)}
                        className="h-9 w-9 p-0 rounded-full"
                        data-testid="next-page"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default Transactions;
